import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeStandbyOutlined, People, PersonOff } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { WorkerTableColumns } from "../../pages/worker/constants";
import { HumanResourceService } from "../../services";
import { EXPORT_WORKER } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { formatExpenseAccountBalance } from "src/utils/formatters";

export const WorkerList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, reloadData }) => {
  const [workers, setWorkers] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "workers",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const hasExportWorker = useHasPermission(EXPORT_WORKER);

  const fetchData = useCallback(
    async (workerFilters = {}) => {
      setLoading(true);
      try {
        const { data: workers } = await HumanResourceService.getEmployees({
          ...workerFilters,
          includeBalance: true,
          type: "WORKER",
          ...(companyId !== null && { companyId }),
        });
        const workersWithFormattedBalance = workers?.rows?.map((worker) => ({
          ...worker,
          account: {
            ...worker.account,
            formattedBalance: worker.account?.balance ? formatExpenseAccountBalance(worker.account.balance) : 0,
          },
        }));
        setWorkers(workersWithFormattedBalance);
        setTotalCount(workers?.count);
      } catch (error) {
        toast.error("Failed to fetch workers");
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(WorkerTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Workers : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Workers : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    navigate(`/worker/${params.row.id}`);
  };

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };

  const rows = workers
    ? workers?.map((worker, index) => ({
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        id: worker.id,
        name: worker?.name,
        primaryPhone: worker?.primaryPhone,
        designation: worker?.designation,
        salary: worker?.salary,
        overtimeHourlyRate: worker?.overtimeHourlyRate,
        balance: worker?.account?.balance,
        formattedBalance: worker?.account?.formattedBalance,
      }))
    : [];
  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton
        // Search data
        placeholder="Search worker"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportWorker}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={WorkerTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
